import { Fragment, useMemo, useState } from 'react';
import { faSparkles } from '@awesome.me/kit-4a6bf60416/icons/classic/solid';
import { useCurrentEditor, useEditorState } from '@tiptap/react';

import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Separator } from '@/components/ui/separator';
import { GenerativeMenu } from './toolbar/ai-menu';
import { AlignSelector } from './toolbar/align-selector';
import { EditorToolbarSurface } from './toolbar/editor-toolbar-surface';
import { LinkSelector } from './toolbar/link-selector';
import { NodeSelector } from './toolbar/node-selector';
import { TableMenu } from './toolbar/table-menu';
import { TextButtons } from './toolbar/text-buttons';

interface RichTextToolbarContentProps {
  headingsOnly?: boolean;
  onToolbarSessionActiveChange: (active: boolean) => void;
}

/** Format toolbar buttons; rendered inside FloatingRichTextToolbar. */
export function RichTextToolbarContent({
  headingsOnly = false,
  onToolbarSessionActiveChange,
}: RichTextToolbarContentProps) {
  const { editor } = useCurrentEditor();
  const [showGenerativeMenu, setShowGenerativeMenu] = useState(false);

  const hasHeadingExtension = useMemo(
    () => editor?.extensionManager.extensions.some((ext) => ext.name === 'heading') ?? false,
    [editor],
  );
  const isInTable = useEditorState({
    editor,
    selector: (ctx) => ctx.editor?.isActive('table') ?? false,
  });

  if (!editor) {
    return null;
  }

  return (
    <EditorToolbarSurface>
      {showGenerativeMenu ? (
        <GenerativeMenu
          onClose={() => {
            setShowGenerativeMenu(false);
            onToolbarSessionActiveChange(false);
          }}
        />
      ) : (
        <Fragment>
          <Button
            size="sm"
            variant="ghost"
            colorScheme="secondary"
            onClick={() => {
              setShowGenerativeMenu(true);
              onToolbarSessionActiveChange(true);
            }}
            aria-label="Open AI menu"
            title="Open AI menu"
          >
            <Icon icon={faSparkles} className="text-primary" />
          </Button>
          <Separator orientation="vertical" />
          {hasHeadingExtension && (
            <>
              <NodeSelector
                headingsOnly={headingsOnly}
                onOpenChange={onToolbarSessionActiveChange}
              />
              {!headingsOnly && <Separator orientation="vertical" />}
            </>
          )}
          {!headingsOnly && (
            <>
              <TextButtons />
              <Separator orientation="vertical" className="h-6" />
              <AlignSelector />
              <Separator orientation="vertical" />
              <LinkSelector onOpenChange={onToolbarSessionActiveChange} />
              {!isInTable && (
                <>
                  <Separator orientation="vertical" />
                  <TableMenu onOpenChange={onToolbarSessionActiveChange} />
                </>
              )}
            </>
          )}
        </Fragment>
      )}
    </EditorToolbarSurface>
  );
}
